import React from 'react'
import { CrmSettings } from '../crm/CrmSettings'
import { NavLink, useParams, Navigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'

import { WidgetSettingsPage } from './WidgetSettingsPage'
import { IntegrationsPage } from './IntegrationsPage'
import { SettingsPage } from './SettingsPage'
import { NoticesPage } from '../notices/NoticesPage'
import { ZohoChatsView } from '../zoho-chats/ZohoChatsView'
import { WhatsAppArchiveView } from '../whatsapp-archive/WhatsAppArchiveView'
import { KBMirrorView } from '../kb-mirror/KBMirrorView'
import { RatingsView } from '../ratings/RatingsView'
import { SupervisorView } from '../supervisor/SupervisorView'
import { AgentDetailPage } from '../supervisor/AgentDetailPage'
import { UnlinkedVisitorsPage } from '../paid-customers/UnlinkedVisitorsPage'
import { PageHeader } from '../shared/PageHeader'

type Role = 'agent' | 'supervisor' | 'admin'

interface Section {
  key: string
  label: string
  group: 'Support' | 'Platform' | 'CRM'
  minRole: Role
  render: () => React.ReactNode
}

/** Blank settings placeholder for not-yet-built (CRM) settings. */
const SettingsPlaceholder: React.FC<{ title: string }> = ({ title }) => (
  <div className="h-full flex flex-col">
    <PageHeader title={title} subtitle="CRM — coming soon" />
    <div className="flex-1 flex items-center justify-center px-6 text-center">
      <p className="text-sm text-txt-tertiary max-w-[360px]">
        {title} is part of the CRM, which is still being built. This section is wired up and reachable —
        its settings will arrive in a future update.
      </p>
    </div>
  </div>
)

/** Widget section = the widget config page + a button to open the live test widget. */
const WidgetSection: React.FC = () => (
  <div className="h-full flex flex-col">
    <div className="flex items-center justify-end px-6 py-2 bg-surface border-b border-border shrink-0">
      <button
        onClick={() => window.open('/api/widget-demo', '_blank')}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-border text-txt-secondary hover:bg-surface-hover transition-colors"
      >
        Open test widget
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
        </svg>
      </button>
    </div>
    <div className="flex-1 min-h-0 overflow-auto">
      <WidgetSettingsPage />
    </div>
  </div>
)

const SECTIONS: Section[] = [
  // Support
  { key: 'widget', label: 'Widget', group: 'Support', minRole: 'admin', render: () => <WidgetSection /> },
  { key: 'notices', label: 'Notices', group: 'Support', minRole: 'admin', render: () => <NoticesPage /> },
  { key: 'zoho-chats', label: 'Zoho Chats', group: 'Support', minRole: 'admin', render: () => <ZohoChatsView /> },
  { key: 'whatsapp-archive', label: 'WhatsApp Archive', group: 'Support', minRole: 'admin', render: () => <WhatsAppArchiveView /> },
  { key: 'kb-mirror', label: 'KB Mirror', group: 'Support', minRole: 'admin', render: () => <KBMirrorView /> },
  { key: 'team', label: 'Team', group: 'Support', minRole: 'supervisor', render: () => <SupervisorView /> },
  { key: 'ratings', label: 'Ratings', group: 'Support', minRole: 'supervisor', render: () => <RatingsView /> },
  { key: 'unlinked', label: 'Unlinked', group: 'Support', minRole: 'supervisor', render: () => <UnlinkedVisitorsPage /> },
  // Platform / access (cross-product)
  { key: 'integrations', label: 'Integrations', group: 'Platform', minRole: 'admin', render: () => <IntegrationsPage /> },
  { key: 'users', label: 'Users', group: 'Platform', minRole: 'admin', render: () => <SettingsPage initialTab="users" hideTabs /> },
  { key: 'roles', label: 'Roles & Permissions', group: 'Platform', minRole: 'admin', render: () => <SettingsPage initialTab="permissions" hideTabs /> },
  { key: 'linking', label: 'Linking Thresholds', group: 'Platform', minRole: 'admin', render: () => <SettingsPage initialTab="linking" hideTabs /> },
  // CRM
  // Lead Settings moved here from the CRM sidebar (Lee, 26 Oct 2026).
  { key: 'lead-settings', label: 'Lead Settings', group: 'CRM', minRole: 'admin', render: () => <CrmSettings /> },
  { key: 'sla-alerts', label: 'Stage SLA Alerts', group: 'CRM', minRole: 'admin', render: () => <SettingsPlaceholder title="Stage SLA Alerts" /> },
  { key: 'webhooks', label: 'Webhooks', group: 'CRM', minRole: 'admin', render: () => <SettingsPlaceholder title="Webhooks" /> },
]


/**
 * Consolidated Settings page reached from the top-bar gear. A left sub-nav lists
 * sections grouped by Support / Platform / CRM; the content area hosts the
 * existing feature page (unchanged) for the active section. Each section keeps
 * its own role requirement — sections the user can't access are hidden, and a
 * direct link to a forbidden section redirects to the first allowed one.
 */
export const SettingsHub: React.FC = () => {
  const { section, agentId } = useParams<{ section?: string; agentId?: string }>()

  // FULL settings module open to everyone (Lee, 29 Jul 2026) — no role filtering.
  const allowed = SECTIONS

  const activeKey = section || allowed[0].key
  const active = allowed.find((s) => s.key === activeKey)

  // Unknown or forbidden section → send to the first section the user can see.
  if (!active) return <Navigate to={`/settings/${allowed[0].key}`} replace />

  const groups: Array<Section['group']> = ['Support', 'Platform', 'CRM']

  return (
    <div className="h-full flex min-h-0">
      {/* Sub-nav */}
      <nav className="w-60 shrink-0 border-r border-border bg-surface overflow-y-auto py-4">
        <p className="px-5 pb-3 text-xs font-bold uppercase tracking-wider text-txt-tertiary">Settings</p>
        {groups.map((g) => {
          const items = allowed.filter((s) => s.group === g)
          if (!items.length) return null
          return (
            <div key={g} className="mb-4">
              <p className="px-5 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-txt-tertiary/70">{g}</p>
              {items.map((s) => (
                <NavLink
                  key={s.key}
                  to={`/settings/${s.key}`}
                  className={({ isActive }) =>
                    `block px-5 py-2 text-sm transition-colors ${
                      isActive || s.key === activeKey
                        ? 'text-brand-600 bg-brand-50 dark:bg-brand-900/20 font-medium border-r-2 border-brand-600'
                        : 'text-txt-secondary hover:text-txt-primary hover:bg-surface-hover'
                    }`
                  }
                >
                  {s.label}
                </NavLink>
              ))}
            </div>
          )
        })}
      </nav>

      {/* Active section.
          key={active.key} is LOAD-BEARING: Users / Roles & Permissions / Linking
          Thresholds all render the same <SettingsPage> component with a different
          initialTab prop. Without the key React reuses the mounted instance, so
          its internal tab state never resets when the section changes — the
          content lagged behind the nav until a refresh. The key forces a fresh
          mount per section. */}
      <div key={active.key} className="flex-1 min-w-0 min-h-0 overflow-auto bg-surface-secondary animate-fade-in">
        {active.key === 'team' && agentId ? <AgentDetailPage /> : active.render()}
      </div>
    </div>
  )
}
